import { memo, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { T } from 'consts/translationKeys'
import { translate } from 'utils/translations'
import { TAppDispatch } from 'store/types'
import { ScrollableElementProvider } from 'containers/SubSidebar/AIVideoContent/context'
import { FETCH_LIMIT } from 'containers/SubSidebar/AiLibContent/consts'
import { aiLibThunks } from 'store/features/aiLibVideo/thunks'
import { aiLibImageThunks } from 'store/features/aiLibImage/thunks'
import { aiLibSelectors } from 'store/features/aiLibVideo/selectors'
import { ButtonWithIcon } from 'common-components'
import { DividerLine } from 'common-components/DividerLine'
import FiltersSection from './FiltersSection'
import { NotFound } from './NotFound'
import { useFetchAiLibHistoryOnScroll } from './hooks/useFetchAiLibHistoryOnScroll'
import * as Styled from './styled'
import Filter from 'assets/svgr-icons/filter.svg'
import { IAIVideoProject } from 'types/aiVideo'
import VideoItem from './VideoItem'
import { usePreviewNavigation } from '../hooks/usePreviewNavigation'

const VideosTab: React.FC = () => {
  const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(
    null
  )
  const isFiltersOpen = useSelector(aiLibSelectors.isFiltersOpen)
  const dispatch = useDispatch<TAppDispatch>()
  const scrollableElementRef = useMemo(
    () => ({ current: scrollContainer }),
    [scrollContainer]
  )
  useFetchAiLibHistoryOnScroll(scrollContainer)

  const isInitialLoading = useSelector(aiLibSelectors.isInitialLoading)
  useEffect(() => {
    if (isInitialLoading && scrollContainer) {
      scrollContainer.scrollTop = 0
    }
  }, [isInitialLoading, scrollContainer])

  const historyList = useSelector(aiLibSelectors.historyList)
  const isLoaded = useSelector(aiLibSelectors.isLoaded)
  const hasActiveFilters = useSelector(aiLibSelectors.hasActiveFilters)
  const activeFiltersCount = useSelector(aiLibSelectors.activeFiltersCount)

  const {
    activePreviewIndex,
    handleOpenPreview,
    handleClosePreview,
    handleNextPreview,
    handlePrevPreview
  } = usePreviewNavigation(historyList.length)

  const handleClearFilters = () => {
    dispatch(aiLibThunks.resetFilters())
  }

  useEffect(() => {
    if (!isLoaded) {
      dispatch(aiLibThunks.getHistory({ offset: 0, limit: FETCH_LIMIT }))
    }
  }, [dispatch, isLoaded])

  return (
    <ScrollableElementProvider scrollableElementRef={scrollableElementRef}>
      <Styled.Container>
        <Styled.Controls>
          <Styled.FilterButton $isOpen={isFiltersOpen}>
            <ButtonWithIcon
              className="filter-btn"
              icon={<Filter />}
              label={`${translate(T.FILTER_LABEL)}${activeFiltersCount > 0 ? ` (${activeFiltersCount})` : ''}`}
              onClick={() => {
                dispatch(aiLibThunks.toggleFiltersOpen())
                dispatch(aiLibImageThunks.toggleFiltersOpen())
              }}
              iconSize="medium"
            />
          </Styled.FilterButton>
          {hasActiveFilters && (
            <Styled.ResetButton onClick={handleClearFilters}>
              {translate(T.RESET_LABEL)}
            </Styled.ResetButton>
          )}
        </Styled.Controls>
        <DividerLine />
        {isFiltersOpen ? <FiltersSection /> : null}

        <Styled.ItemsContainer ref={setScrollContainer}>
          {historyList.length === 0 && isLoaded ? (
            <NotFound />
          ) : (
            <>
              {historyList.map((item: IAIVideoProject, index: number) => (
                <VideoItem
                  key={item.id}
                  item={item}
                  index={index}
                  isPreviewOpen={activePreviewIndex === index}
                  onOpenPreview={handleOpenPreview}
                  onClosePreview={handleClosePreview}
                  onNext={handleNextPreview}
                  onPrev={handlePrevPreview}
                  hasNext={index < historyList.length - 1}
                  hasPrev={index > 0}
                />
              ))}
            </>
          )}
        </Styled.ItemsContainer>
      </Styled.Container>
    </ScrollableElementProvider>
  )
}

export default memo(VideosTab)
